import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { ReactNode } from 'react'

import { APP, NAV } from '../labels'
import { ACCENT, BAND, CANVAS, INK, INK_FAINT, INK_SOFT, LINE, PAPER, STICKER, FONT_MONO } from '../theme'

export type ViewKey = 'today' | 'calendar' | 'refresh' | 'roadmap' | 'backlog' | 'rgm'

interface Props {
  view: ViewKey
  onViewChange: (v: ViewKey) => void
  attentionCount: number
  userSlot?: ReactNode
  children: ReactNode
}

const TABS: [ViewKey, string][] = [
  ['today', NAV.today],
  ['calendar', NAV.calendar],
  ['refresh', NAV.refresh],
  ['roadmap', NAV.roadmap],
  ['backlog', NAV.backlog],
  ['rgm', NAV.rgm],
]

/**
 * Cabecera fija y navegación en píldoras.
 *
 * Las pestañas subrayadas de MUI se leen como "documento con secciones"; esto
 * se lee como un control físico, que es lo que la navegación de un tablero
 * operativo debería parecer. La pastilla activa sube al papel (fondo claro y
 * relieve) y las demás se quedan hundidas en la banda.
 */
export function AppShell({ view, onViewChange, attentionCount, userSlot, children }: Props) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: CANVAS }}>
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'rgba(250, 247, 243, 0.86)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Container maxWidth="xl">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 1.75, gap: 2, flexWrap: 'wrap' }}
          >
            <Wordmark />
            {userSlot}
          </Stack>

          <Stack direction="row" sx={{ pb: 1.5 }}>
            <Stack
              direction="row"
              sx={{
                bgcolor: BAND,
                borderRadius: 999,
                p: 0.5,
                gap: 0.5,
                border: `1px solid ${LINE}`,
              }}
            >
              {TABS.map(([key, label]) => (
                <NavPill
                  key={key}
                  label={label}
                  active={view === key}
                  badge={key === 'today' ? attentionCount : 0}
                  onClick={() => onViewChange(key)}
                />
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {children}
      </Container>
    </Box>
  )
}

/** La marca: un punto de acento y dos renglones. Nada más. */
function Wordmark() {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '10px',
          bgcolor: INK,
          color: CANVAS,
          display: 'grid',
          placeItems: 'center',
          fontFamily: FONT_MONO,
          fontSize: 15,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        ◗
      </Box>
      <Box>
        <Typography
          component="h1"
          sx={{
            fontSize: '1.0625rem',
            fontWeight: 700,
            letterSpacing: '-0.025em',
            lineHeight: 1.1,
            color: INK,
          }}
        >
          {APP.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_MONO,
            fontSize: '0.625rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: INK_FAINT,
          }}
        >
          {APP.subtitle}
        </Typography>
      </Box>
    </Stack>
  )
}

function NavPill({
  label,
  active,
  badge,
  onClick,
}: {
  label: string
  active: boolean
  badge: number
  onClick: () => void
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        borderRadius: 999,
        px: 2,
        py: 0.85,
        gap: 0.75,
        bgcolor: active ? PAPER : 'transparent',
        color: active ? INK : INK_SOFT,
        boxShadow: active ? STICKER : 'none',
        border: `1px solid ${active ? LINE : 'transparent'}`,
        fontFamily: '"Archivo", sans-serif',
        fontSize: '0.875rem',
        fontWeight: active ? 600 : 500,
        letterSpacing: '-0.01em',
        transition: 'background-color 180ms, color 180ms, box-shadow 180ms',
        '&:hover': { color: INK, bgcolor: active ? PAPER : 'rgba(255,253,250,0.6)' },
      }}
    >
      {label}
      {badge > 0 && (
        <Box
          component="span"
          sx={{
            bgcolor: ACCENT,
            color: '#FFFDFA',
            borderRadius: 999,
            minWidth: 18,
            height: 18,
            px: 0.5,
            display: 'grid',
            placeItems: 'center',
            fontFamily: FONT_MONO,
            fontSize: '0.625rem',
            fontWeight: 600,
          }}
        >
          {badge}
        </Box>
      )}
    </ButtonBase>
  )
}
